/**
 * Authentication Service for SSO Integration (Pure React)
 * Uses OIDC implicit (id_token) flow client-side only. No backend required.
 */

export interface UserInfo {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  roles: string[];
}

export interface AuthStatus {
  isAuthenticated: boolean;
  user?: UserInfo;
  error?: string;
}

class AuthService {
  private static instance: AuthService;
  private appUrl: string;
  private backendUrl: string;
  private storageUserKey = 'ssoUser';
  private storageStateKey = 'ssoState';
  private storageNonceKey = 'ssoNonce';
  private storageTokenKey = 'ssoAccessToken';

  private constructor() {
    const hostname = window.location.hostname;
    const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
    this.appUrl = isDevelopment
      ? (import.meta.env.VITE_APP_URL_DEV || 'http://localhost:3001')
      : (import.meta.env.VITE_APP_URL_PRODUCTION || `${window.location.protocol}//${hostname}`);

    this.backendUrl = isDevelopment
      ? (import.meta.env.VITE_API_URL_DEV || 'http://localhost:5000')
      : (import.meta.env.VITE_API_URL_PRODUCTION || `${window.location.protocol}//${hostname}`);
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Token Management
  setAccessToken(token: string) {
    sessionStorage.setItem(this.storageTokenKey, token);
  }

  getAccessToken() {
    return sessionStorage.getItem(this.storageTokenKey);
  }

  private getAuthenticatedHeaders(): HeadersInit {
    const headers: any = {
      'Content-Type': 'application/json'
    };
    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Generate SSO sign-in URL directly (for testing or direct SSO flow)
   * @param returnUrl Optional return URL after authentication
   * @returns Complete SSO authorization URL
   */
  generateSSOSignInUrl(returnUrl?: string): string {
    const authority = import.meta.env.VITE_SSO_AUTHORITY || 'https://ssodev.commvault.com';
    const clientId = import.meta.env.VITE_SSO_CLIENT_ID || 'customerportal_dev';
    const redirectUri = this.appUrl + '/signin-oidc';
    const scope = import.meta.env.VITE_SSO_SCOPE || 'openid profile email';
    // Pure SPA: use implicit flow so id_token arrives in fragment
    const responseType = 'id_token';
    const responseMode = 'fragment';

    const state = this.generateState();
    const nonce = this.generateNonce();
    sessionStorage.setItem(this.storageStateKey, state);
    sessionStorage.setItem(this.storageNonceKey, nonce);

    if (returnUrl) {
      sessionStorage.setItem('ssoReturnUrl', returnUrl);
    }

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: responseType,
      scope: scope,
      state: state,
      nonce: nonce
    });

    const authUrl = `${authority}/connect/authorize?${params.toString()}`;
    return authUrl;
  }

  /**
   * Generate random state for CSRF protection
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate random nonce for token validation
   */
  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  async initiateSSOLogin(returnUrl?: string): Promise<void> {
    // Use provided returnUrl or default to home page  
    const redirectUrl = returnUrl || window.location.href;

    console.log('[AuthService] Initiating Backend SSO login...');
    console.log('[AuthService] Return URL:', redirectUrl);

    // Save return URL for the client-side callback page to use later
    // Extract relative path if possible, or use full URL
    try {
      const urlObj = new URL(redirectUrl, window.location.origin);
      if (urlObj.origin === window.location.origin) {
        sessionStorage.setItem('ssoReturnUrl', urlObj.pathname + urlObj.search + urlObj.hash);
      } else {
        sessionStorage.setItem('ssoReturnUrl', redirectUrl);
      }
    } catch (e) {
      sessionStorage.setItem('ssoReturnUrl', redirectUrl);
    }

    try {
      // Redirect to Backend Login Endpoint
      // The backend will handle the OIDC Challenge and Redirect back to us
      const loginUrl = `${this.backendUrl}/api/Account/SSOLogin?redirectUri=${encodeURIComponent(redirectUrl)}`;
      window.location.href = loginUrl;
    } catch (error) {
      console.error('[AuthService] Error initiating SSO login:', error);
      throw error;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const userRaw = sessionStorage.getItem(this.storageUserKey);
    return !!userRaw;
  }

  async getAuthStatus(): Promise<AuthStatus> {
    try {
      const response = await fetch(`${this.backendUrl}/api/Account/AuthStatus`, {
        method: 'GET',
        credentials: 'include', // Important: Send cookies
        headers: this.getAuthenticatedHeaders()
      });

      if (!response.ok) {
        return { isAuthenticated: false };
      }

      const data = await response.json();

      if (data.isAuthenticated) {
        // Cache user in session storage for components that might read it synchronously (legacy support)
        sessionStorage.setItem(this.storageUserKey, JSON.stringify(data.user));
        
        // Save the raw id_token for the logout hint to bypass SSO prompts
        if (data.user && data.user.idToken) {
            this.setAccessToken(data.user.idToken);
        }
        
        return { isAuthenticated: true, user: data.user };
      } else {
        sessionStorage.removeItem(this.storageUserKey);
        return { isAuthenticated: false };
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      return { isAuthenticated: false };
    }
  }

  async getCurrentUser(): Promise<UserInfo | null> {
    const status = await this.getAuthStatus();
    return status.user || null;
  }

  async hasRole(role: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.backendUrl}/api/Account/HasRole/${encodeURIComponent(role)}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: this.getAuthenticatedHeaders()
        }
      );

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.hasRole === true;
    } catch (error) {
      console.error(`Error checking role ${role}:`, error);
      return false;
    }
  }

  logout(): void {
    const token = this.getAccessToken();
    const ssoUrl = import.meta.env.VITE_SSO_AUTHORITY || 'https://ssodev.commvault.com';
    this.clearCache();
    
    // 1. Silent backend local logout
    let backendUrl = `${this.backendUrl}/api/Account/Logout?localOnly=true`;
    if (token) {
        backendUrl += `&id_token_hint=${encodeURIComponent(token)}`;
    }
    
    // Fire and forget local backend session clear
    fetch(backendUrl, { credentials: 'include' }).catch(e => console.error(e));

    // 2. Perform the SSO logout in the background (hidden iframe)
    try {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = `${ssoUrl}/connect/endsession?id_token_hint=${encodeURIComponent(token || '')}`;
        document.body.appendChild(iframe);
    } catch (e) {
        console.error('Failed to append logout iframe', e);
    }

    // 3. Redirect to login page immediately as requested
    setTimeout(() => {
        window.location.href = '/login';
    }, 300);
  }

  clearCache(): void {
    try {
      sessionStorage.removeItem(this.storageUserKey);
      sessionStorage.removeItem(this.storageStateKey);
      sessionStorage.removeItem(this.storageNonceKey);
      sessionStorage.removeItem('ssoReturnUrl');
      sessionStorage.removeItem(this.storageTokenKey);
    } catch (error) {
      console.warn('Error clearing auth cache:', error);
    }
  }

  async handleSSOCallback(): Promise<AuthStatus> {
    return this.getAuthStatus();
  }

  async refreshSession(): Promise<boolean> {
    try {
      const status = await this.getAuthStatus();
      return status.isAuthenticated;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  }

  getAppUrl(): string {
    return this.appUrl;
  }

  /**
   * Check if running in development mode
   */
  isDevelopment(): boolean {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  }

  /**
   * Check if running in production mode
   */
  isProduction(): boolean {
    return !this.isDevelopment();
  }
}

export const authService = AuthService.getInstance();